import { apiClient, getErrorMessage } from './client'

export { getErrorMessage }

export interface TitleClientRef {
  clientId: string
}

export interface Title {
  _id: string
  name: string
  aliases: string[]
  releaseYear?: number
  clients: TitleClientRef[]
  externalIds: Record<string, string>
  isActive: boolean
  createdBy: { userId: string; appId: string }
  updatedBy: { userId: string; appId: string }
  createdAt: string
  updatedAt: string
}

export interface TitleSearchResult {
  _id: string
  name: string
  aliases: string[]
  releaseYear?: number
  clients: TitleClientRef[]
  score: number
}

export interface TitlesListResponse {
  titles: Title[]
  total: number
  page: number
  pages: number
}

export interface TitleDuplicateWarning {
  status: 'duplicates_found'
  message: string
  matches: TitleSearchResult[]
}

export interface CreateTitleData {
  name: string
  aliases?: string[]
  releaseYear?: number
  clients?: TitleClientRef[]
  externalIds?: Record<string, string>
  confirmCreate?: boolean
}

export interface UpdateTitleData {
  name?: string
  aliases?: string[]
  releaseYear?: number | null
  clients?: TitleClientRef[]
  isActive?: boolean
}

export type CreateTitleResult =
  | { created: true; title: Title }
  | { created: false; duplicateWarning: TitleDuplicateWarning }

export async function listTitles(params?: {
  clientId?: string
  isActive?: string
  search?: string
  releaseYear?: number
  page?: number
  limit?: number
}): Promise<TitlesListResponse> {
  const response = await apiClient.get<TitlesListResponse>('/admin/titles', { params })
  return response.data
}

export async function getTitle(id: string): Promise<Title> {
  const response = await apiClient.get<{ title: Title }>(`/admin/titles/${id}`)
  return response.data.title
}

export async function createTitle(data: CreateTitleData): Promise<CreateTitleResult> {
  const response = await apiClient.post<{ title?: Title } & Partial<TitleDuplicateWarning>>(
    '/admin/titles',
    data
  )

  if (response.data.status === 'duplicates_found') {
    return { created: false, duplicateWarning: response.data as TitleDuplicateWarning }
  }

  return { created: true, title: response.data.title! }
}

export async function updateTitle(id: string, data: UpdateTitleData): Promise<Title> {
  const response = await apiClient.patch<{ title: Title }>(`/admin/titles/${id}`, data)
  return response.data.title
}

export async function deleteTitle(id: string): Promise<void> {
  await apiClient.delete(`/admin/titles/${id}`)
}

export async function setTitleExternalId(id: string, system: string, value: string): Promise<Title> {
  const response = await apiClient.put<{ title: Title }>(`/admin/titles/${id}/external-ids/${system}`, { id: value })
  return response.data.title
}

export async function removeTitleExternalId(id: string, system: string): Promise<Title> {
  const response = await apiClient.delete<{ title: Title }>(`/admin/titles/${id}/external-ids/${system}`)
  return response.data.title
}
